import {useEffect, useState} from "react";
import {Heart} from "lucide-react";
import {Portal} from "./Portal";

/**
 * Анимация сердца после лайка (issue #676, п.13).
 *
 * Заменяет текстовый тост «Лайк отправлен» в сценариях лайка: сердечко —
 * та же бренд-иконка, что на кнопке «нравится» — всплывает от кнопки лайка,
 * пульсирует и растворяется. Никакой текстовой плашки и перехвата ввода:
 * `pointer-events: none` на всём оверлее, лента остаётся интерактивной.
 *
 * Переиспользуемый низкоуровневый примитив: `showLikeHeart()` адресует
 * всплытие (по умолчанию — над зоной кнопок карточки). Тот же механизм
 * подойдёт суперлайку — вызов с другим цветом/точкой старта добавляется
 * параметром, без новых компонентов.
 *
 * Монтируется один раз в `App.tsx` рядом с `AppToaster` (тот же `Portal`
 * на body: внутри `#root` с `position: fixed` оверлей был бы зажат
 * контекстом наложения карточек). prefers-reduced-motion: анимация
 * заменяется коротким кроссфейдом без движения — см. `.rla-like-heart-*`
 * в `styles/app.css`.
 */

const LIKE_HEART_EVENT = "rla:like-heart";

export interface LikeHeartOptions {
    /** Точка старта в координатах окна (например, центр кнопки лайка). */
    origin?: {x: number; y: number};
    /** «Суперлайк» — тот же жест, синяя смысловая линия (токен `--rla-blue`). */
    super?: boolean;
}

/** Запустить анимацию сердца. Возврат мгновенный, интерфейс не блокируется. */
export function showLikeHeart(options: LikeHeartOptions = {}): void {
    window.dispatchEvent(new CustomEvent(LIKE_HEART_EVENT, {detail: options}));
}

interface Burst {
    id: number;
    origin: {x: number; y: number};
    super: boolean;
}

export function LikeHeartOverlay() {
    const [bursts, setBursts] = useState<Burst[]>([]);

    useEffect(() => {
        let nextId = 0;
        const handle = (event: Event) => {
            const detail = (event as CustomEvent<LikeHeartOptions>).detail ?? {};
            const origin = detail.origin ?? defaultOrigin();
            const id = nextId++;
            setBursts((prev) => [...prev, {id, origin, super: Boolean(detail.super)}]);
            // Длительность совпадает с CSS-анимацией (.rla-like-heart: 1200ms).
            window.setTimeout(() => {
                setBursts((prev) => prev.filter((burst) => burst.id !== id));
            }, 1200);
        };
        window.addEventListener(LIKE_HEART_EVENT, handle);
        return () => window.removeEventListener(LIKE_HEART_EVENT, handle);
    }, []);

    return (
        <Portal>
            <div className="rla-like-heart-layer" aria-hidden>
                {bursts.map((burst) => (
                    <span
                        key={burst.id}
                        className={`rla-like-heart ${burst.super ? "rla-like-heart--super" : ""}`}                        style={{left: burst.origin.x, top: burst.origin.y}}
                    >
                        <Heart size={44} strokeWidth={2.25} className="fill-current" aria-hidden/>
                    </span>
                ))}
            </div>
        </Portal>
    );
}

/** Без явной точки старта — над зоной действий карточки (центр низа вьюпорта). */
function defaultOrigin(): {x: number; y: number} {
    return {
        x: window.innerWidth / 2,
        y: window.innerHeight - 140,
    };
}
